import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Reusable Accordion Component.
 * Provides an expandable/collapsible section for content.
 *
 * @param {object} props - Component props.
 * @param {string} props.title - The title displayed on the accordion header.
 * @param {React.ReactNode} props.children - The content to be displayed inside the accordion.
 * @param {boolean} [props.defaultOpen=false] - If true, the accordion is open by default.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the accordion container.
 * @param {string} [props.headerClassName=''] - Additional custom Tailwind classes for the header.
 * @param {string} [props.contentClassName=''] - Additional custom Tailwind classes for the content area.
 */
const Accordion = ({ title, children, defaultOpen = false, className = '', headerClassName = '', contentClassName = '' }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className={`border border-gray-200 rounded-lg shadow-sm overflow-hidden ${className}`}>
            <button
                type="button"
                className={`flex justify-between items-center w-full p-4 text-left font-semibold text-text-primary bg-gray-100 hover:bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue ${headerClassName}`}
                onClick={toggleAccordion}
                aria-expanded={isOpen}
            >
                <span>{title}</span>
                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {isOpen && (
                <div className={`p-4 bg-white ${contentClassName}`}>
                    {children}
                </div>
            )}
        </div>
    );
};

export default Accordion;
